import { useEffect, useRef, useState } from "react"

import { INITIAL_REVEAL_COUNT } from "../sortaballs-10-colors/use-incremental-replay"
import { type ChallengeData, completeCreateChallenge } from "./api"
import { createFlowStorage } from "./battle-storage"
import { getSolvedTimestamp } from "./get-solved-timestamp"
import { PENALTY_FREE_MOVES, readMoveCount } from "./read-move-count"
import { useRestoreLevel } from "./use-restore-level"

export interface CreateBattle {
  challenge: ChallengeData | null
  // The embedded game's wrapper — clicks on its Undo / help buttons are
  // captured here so the challenge can count them.
  containerRef: React.RefObject<HTMLDivElement | null>
  displaySeconds: number
  hintsUsed: number
  pause: ()=> void
  paused: boolean
  penaltyCount: number
  resume: ()=> void
  solved: boolean
  start: (challenge: ChallengeData)=> void
  stop: ()=> void
  togglePause: ()=> void
  undoCount: number
}

// The created challenge has no opponent: instead of a countdown there is a
// stopwatch (elapsed since the game mounted, plus a 1s penalty per undo) and
// completing the puzzle reports {id, time, hints_used}.
export function useCreateBattle(happy: boolean): CreateBattle {
  const { restore, snapshot } = useRestoreLevel()
  const stored = createFlowStorage.loadBattle()
  const initialStartedAt = stored
    ? stored.startedAt + (stored.pausedAt ? Date.now() - stored.pausedAt : 0)
    : 0
  const [challenge, setChallenge] = useState<ChallengeData | null>(
    stored?.challenge ?? null,
  )
  const [elapsedSeconds, setElapsedSeconds] = useState(()=> {
    if (!stored) return 0
    return Math.floor((Date.now() - initialStartedAt) / 1000)
  })
  const [undoCount, setUndoCount] = useState(stored?.undoCount ?? 0)
  const [hintsUsed, setHintsUsed] = useState(stored?.hintsUsed ?? 0)
  const [penaltyCount, setPenaltyCount] = useState(0)
  const [paused, setPaused] = useState(false)
  const [solved, setSolved] = useState(false)
  const startedAtRef = useRef(initialStartedAt)
  const undoRef = useRef(undoCount)
  const hintsRef = useRef(hintsUsed)
  const pausedRef = useRef(false)
  const pausedAtRef = useRef(stored?.pausedAt ?? 0)
  const solvedRef = useRef(false)
  const initialStampRef = useRef<string | undefined>(
    stored?.challenge ? getSolvedTimestamp(stored.challenge) : undefined,
  )
  const containerRef = useRef<HTMLDivElement | null>(null)

  const pause = (): void=> {
    if (!challenge || pausedRef.current || solvedRef.current) return
    pausedAtRef.current = Date.now()
    pausedRef.current = true
    setPaused(true)
    createFlowStorage.saveBattle({
      challenge,
      hintsUsed: hintsRef.current,
      lost: false,
      paused: true,
      pausedAt: pausedAtRef.current,
      startedAt: startedAtRef.current,
      undoCount: undoRef.current,
    })
  }

  const resume = (): void=> {
    if (!pausedRef.current) return
    const battleChallenge = challenge ?? stored?.challenge
    if (!battleChallenge) {
      pausedRef.current = false
      setPaused(false)
      return
    }
    startedAtRef.current += Date.now() - pausedAtRef.current
    pausedRef.current = false
    setPaused(false)
    createFlowStorage.saveBattle({
      challenge: battleChallenge,
      hintsUsed: hintsRef.current,
      lost: false,
      paused: false,
      startedAt: startedAtRef.current,
      undoCount: undoRef.current,
    })
  }

  const togglePause = (): void=> {
    if (pausedRef.current) {
      resume()
    } else {
      pause()
    }
  }

  const start = (next: ChallengeData): void=> {
    const startedAt = Date.now()
    startedAtRef.current = startedAt
    undoRef.current = 0
    hintsRef.current = 0
    solvedRef.current = false
    pausedRef.current = false
    pausedAtRef.current = 0
    initialStampRef.current = getSolvedTimestamp(next)
    snapshot(next.hard_mode)
    setChallenge(next)
    setElapsedSeconds(0)
    setPaused(false)
    setUndoCount(0)
    setHintsUsed(0)
    setPenaltyCount(0)
    setSolved(false)
    createFlowStorage.saveBattle({
      challenge: next,
      hintsUsed: 0,
      lost: false,
      startedAt,
      undoCount: 0,
    })
  }

  const stop = (): void=> {
    restore()
    createFlowStorage.clearBattle()
    startedAtRef.current = 0
    solvedRef.current = false
    pausedRef.current = false
    setChallenge(null)
    setPaused(false)
    setSolved(false)
    setPenaltyCount(0)
  }

  // Count the embedded game's Undo / help clicks (capture phase: the buttons
  // live inside Sortaballs10ColorsApp / Sortaballs8ColorsApp, whose own handlers must still run).
  useEffect(()=> {
    const container = containerRef.current
    if (container === null || !challenge) return
    const onClick = (event: MouseEvent): void=> {
      // SAFETY: DOM event target is always an HTMLElement
      const button = (event.target as HTMLElement).closest("button")
      const title = button?.getAttribute("title") ?? ""
      if (title === "Undo") {
        // No penalty until the player has made enough moves — the undo just
        // rewinds a still-short attempt.
        if (readMoveCount(container) >= PENALTY_FREE_MOVES) {
          undoRef.current += 1
          setUndoCount(undoRef.current)
          setPenaltyCount(prev=> prev + 1)
        }
      } else if (title.startsWith("Show")) {
        // Each press reveals a batch of correct moves, not a single one. The
        // game's reveal logic shows INITIAL_REVEAL_COUNT (5) on the first press
        // then +1 per press, so `used_hints` must report the number of correct
        // moves revealed — not the number of button presses.
        hintsRef.current =
          hintsRef.current === 0 ? INITIAL_REVEAL_COUNT : hintsRef.current + 1
        setHintsUsed(hintsRef.current)
      }
    }
    container.addEventListener("click", onClick, true)
    return ()=> container.removeEventListener("click", onClick, true)
  }, [challenge, happy])

  useEffect(()=> {
    if (challenge === null || solved || paused) return
    const intervalId = setInterval(()=> {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000)
      setElapsedSeconds(elapsed)

      const stamp = getSolvedTimestamp(challenge)
      const playerSolved =
        stamp !== undefined &&
        stamp !== initialStampRef.current &&
        solvedRef.current === false
      if (playerSolved) {
        solvedRef.current = true
        completeCreateChallenge({
          hints_used: hintsRef.current,
          id: challenge.id,
          time: elapsed + undoRef.current,
        })
        setSolved(true)
        createFlowStorage.clearBattle()
      }
    }, 1000)
    return ()=> clearInterval(intervalId)
  }, [challenge, solved, paused])

  return {
    challenge,
    containerRef,
    displaySeconds: elapsedSeconds + undoCount,
    hintsUsed,
    pause,
    paused,
    penaltyCount,
    resume,
    solved,
    start,
    stop,
    togglePause,
    undoCount,
  }
}
